import React, { useEffect, useState } from "react"
import Image from "next/image"
import styled from "styled-components"
import { useRouter } from "next/router"
import { useTranslations } from "next-intl"
import useInject from "~/hooks/useInject"
import { FETCHER_CITY_STATES, STATES } from "~/utilities/constants"
import { SEARCH_OPTIONS_STATES_LOGOS as TRAFFIC_STATES_LOGOS } from "~/components/traffic/lookup/Forms/Elements/searchConfig"
import { SEARCH_OPTIONS_STATES_LOGOS as DIVORCE_STATES_LOGOS } from "~/components/divorce/lookup/search/config"
import WinItLogo from "~/public/static/images/svg/logo-shadow.svg"

const DOTResultsLogo = styled.img`
  height: 35px;
  display: block;
`
const StateLogoImg = styled.img`
  display: block;
  align-self: flex-start;
  max-width: 100%;
  height: auto;
`
const LogoWrapper = styled.div`
  display: block;
  align-self: flex-start;
  ${(props) =>
    (props.isSFLogo ||
      props.isSBOLogo ||
      props.isSBALogo ||
      props.isCALOrange ||
      props.isCAL ||
      props.isCALState ||
      props.isFLMiami ||
      props.isGA ||
      props.isVentura ||
      props.isNassau ||
      props.isPennsylvania ||
      props.isWashington ||
      props.isWashingtonSeattle) &&
    `
    overflow: hidden;
    width: ${props.width}px;
    img {
      display: block;
      transform: translateX(${props.translateX}px);
      max-width: none;
    }
  `}
`
const DivLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  div.poweredByDiv {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  span.poweredBy {
    font-size: 0.7rem;
    color: #6c757d;
  }

  a.poweredByLink {
    display: flex;
    align-items: center;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    font-size: 12px;
    color: #6c757d;
    padding-inline: 8px;
  }
`

const mapStore = (store) => ({
  storedTrafficStateName: store.trafficLookup.fetcherName,
  storedDivorceStateName: store.divorceLookup.fetcherName,
  cameFromDOTEmail: store.trafficLookup.cameFromDOTEmail,
  checkStateAffiliatedWithWinitLaw:
    store.trafficLookup.checkStateAffiliatedWithWinitLaw,
})

const StateLogo = ({ fetcherType = "traffic" }) => {
  const {
    storedTrafficStateName,
    storedDivorceStateName,
    cameFromDOTEmail,
    checkStateAffiliatedWithWinitLaw,
  } = useInject(mapStore)
  const [isWinitLawAffiliated, setIsWinitLawAffiliated] = useState(false)
  const router = useRouter()
  const translate = useTranslations("header")

  const FETCHER_TYPE_OPTIONS = {
    traffic: {
      logos: TRAFFIC_STATES_LOGOS,
      itemName: "Tickets",
      fetchers: FETCHER_CITY_STATES.traffic,
      storedStateName: storedTrafficStateName,
      checkWinitLawAffiliation: () =>
        checkStateAffiliatedWithWinitLaw(storedTrafficStateName),
    },
    divorce: {
      logos: DIVORCE_STATES_LOGOS,
      itemName: "Cases",
      fetchers: FETCHER_CITY_STATES.divorce,
      storedStateName: storedDivorceStateName,
      checkWinitLawAffiliation: () => true,
    },
  }

  const {
    logos,
    itemName,
    fetchers,
    storedStateName,
    checkWinitLawAffiliation,
  } = FETCHER_TYPE_OPTIONS[fetcherType] || FETCHER_TYPE_OPTIONS.traffic

  const { state: queryStateName } = router.query
  const fetcherName = storedStateName || fetchers[queryStateName]?.fetcherName
  const { logoUrl, width, height } = logos[fetcherName] || logos.DEFAULT
  const alt = `Lookup ${STATES[fetcherName]} State ${itemName}`
  const isSFLogo = fetcherName === "CAL_SAN_FRANCISCO"
  const isSBOLogo = fetcherName === "CAL_SAN_BERNARDINO"
  const isSBALogo = fetcherName === "CAL_SANTA_BARBARA"
  const isCALOrange = fetcherName === "CAL_ORANGE"
  const isCAL = fetcherName === "CAL"
  const isCALState = fetcherName === "CAL_STATE"
  const isFLMiami = fetcherName === "FL_MIAMI"
  const isGA = fetcherName === "GA"
  const isVentura = fetcherName === "CAL_VENTURA"
  const isNassau = fetcherName === "NY_NASSAU"
  const isPennsylvania = fetcherName === "PA_PHILADELPHIA"
  const isWashington = fetcherName === "WA"
  const isWashingtonSeattle = fetcherName === "WA_NEW_SEATTLE"
  
  // Determine translate value: -30px for GA, Nassau, and Miami; -40px for others
  const translateX = isGA || isNassau || isFLMiami || isWashington || isWashingtonSeattle ? -30 : -40

  useEffect(() => {
    const result = checkWinitLawAffiliation(storedStateName)

    setIsWinitLawAffiliated(result)
  }, [])

  return cameFromDOTEmail ? (
    <DOTResultsLogo src={WinItLogo} alt="WinIt Logo" />
  ) : (
    <DivLogo>
      {isSFLogo ||
      isSBOLogo ||
      isSBALogo ||
      isCALOrange ||
      isCAL ||
      isCALState ||
      isFLMiami ||
      isGA ||
      isVentura ||
      isNassau ||
      isPennsylvania ||
      isWashington ||
      isWashingtonSeattle ? (
        <LogoWrapper
          isSFLogo={isSFLogo}
          isSBOLogo={isSBOLogo}
          isSBALogo={isSBALogo}
          isCALOrange={isCALOrange}
          isCAL={isCAL}
          isCALState={isCALState}
          isFLMiami={isFLMiami}
          isGA={isGA}
          isVentura={isVentura}
          isNassau={isNassau}
          isPennsylvania={isPennsylvania}
          isWashington={isWashington}
          isWashingtonSeattle={isWashingtonSeattle}
          translateX={translateX}
          width={width}
          height={height}
        >
          <StateLogoImg width={width} height={height} src={logoUrl} alt={alt} />
        </LogoWrapper>
      ) : (
        <StateLogoImg width={width} height={height} src={logoUrl} alt={alt} />
      )}

      <div className="poweredByDiv">
        <span className="poweredBy">{translate("poweredBy")}</span>
        <a
          className="poweredByLink"
          href="https://www.appwinit.com/"
          target="_blank"
          rel="noreferrer"
        >
          <Image src={WinItLogo} alt="WinIt LLC Logo" width={80} height={25} />
          <strong>*</strong>
        </a>
      </div>
    </DivLogo>
  )
}

export default StateLogo
